import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  Snackbar,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Science,
  CheckCircle,
  Assignment,
  Timer,
  Save,
  Send,
  Refresh,
  Edit,
  Visibility,
  Calculate,
  ExpandMore,
  ExpandLess,
  PriorityHigh,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, SampleStatus, Priority } from '../../services/samplesService';
import apiService from '../../services/api';

// Granulométrie mesh sizes (in μm)
const MESH_SIZES = [10000, 8000, 5000, 3150, 2000, 1000, 800, 500, 300, 200, 125, 80, 45, 25, -25];

// Physico-Chimique Elements
const PHYSICO_ELEMENTS = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'GRANULO', 'D80'];

interface GranulometrieRow {
  size_um: number;
  mass_g: number | null;
  refus_percent: number;
  cumulative_refus: number;
  cumulative_passing: number;
}

interface PhysicoChimiqueResults {
  densite?: number | null;
  h2o?: number | null;
  paf?: number | null;
  mo?: number | null;
  ph?: number | null;
  blancheur_l?: number | null;
  blancheur_a?: number | null;
  blancheur_b?: number | null;
  granulometrie?: GranulometrieRow[];
  d80?: number | null;
  total_mass?: number | null;
}

interface BatchData {
  batchNumber: string;
  samples: Sample[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const PhysicoChimiqueAnalystDashboard: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState<BatchData | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  
  // Stats
  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
  });
  
  // Results state for batch analysis
  const [batchResults, setBatchResults] = useState<Record<string, PhysicoChimiqueResults>>({});

  // Helper function to group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  // Get Physico-Chimique elements from sample
  // 🔥 FIX: Filter element_lab_routing by selected_elements (respects per-sample customization)
  const getPhysicoElementsFromSample = (sample: Sample): string[] => {
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLab = sample.metadonnees?.multi_lab_workflow === true || 
                       Object.keys(elementRouting).length > 0;
    
    // For multi-lab samples: filter selected_elements by routing to Physico
    if (isMultiLab && selectedElements.length > 0 && Object.keys(elementRouting).length > 0) {
      const physicoElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_PHYSICO_CHIMIQUE'
      );
      
      if (physicoElements.length > 0) {
        console.log(`🔍 Physico Analyst: Elements for ${sample.sample_id} (filtered by selected_elements):`, physicoElements);
        return physicoElements;
      }
    }
    
    // Fallback: Check selected_elements for Physico-Chimique elements
    if (selectedElements.length > 0) {
      return selectedElements.filter((el: string) => PHYSICO_ELEMENTS.includes(el));
    }
    
    return [];
  };

  // Check if sample has Physico-Chimique elements
  const hasPhysicoElements = (sample: Sample): boolean => {
    return getPhysicoElementsFromSample(sample).length > 0;
  };

  // Load samples
  const loadSamples = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🔥 INDEPENDENT WORKFLOW: Physico-Chimique works PARALLEL to other labs!
      // We load samples from ALL possible statuses and filter by Physico criteria
      // A sample could be in ICP_PREP and still need Physico analysis
      const response = await samplesService.getSamples(1, 2000, {
        status: [
          // Direct Physico-Chimique status
          SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE,
          // All other lab statuses (multi-lab samples)
          SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_ICP_ANALYSE,
          SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_SAA_ANALYSE,
          SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_AU_ANALYSE,
          SampleStatus.PREPARATION_MECANIQUE,
          // Samples might be in validation/ready but Physico not done yet
          SampleStatus.VALIDATION_SERVICE,
        ],
      });
      
      // 🔥 INDEPENDENT WORKFLOW: Filter samples that need Physico-Chimique analysis
      // Defensive: support string/boolean flags and ensure physico routing/elements exist
      const physicoSamples = (response.samples || []).filter((sample: Sample) => {
        const analysisCompleted =
          sample.metadonnees?.physico_chimique_analysis_completed === true ||
          sample.metadonnees?.physico_chimique_analysis_completed === 'true';
        const validated =
          sample.metadonnees?.physico_chimique_validated === true ||
          sample.metadonnees?.physico_chimique_validated === 'true';
        if (analysisCompleted && validated) return false;
        
        // Check if sample has Physico-Chimique elements in routing or legacy selection
        const elementRouting = sample.metadonnees?.element_lab_routing || {};
        const hasPhysicoRoutedElements = Object.values(elementRouting).includes('LABORATOIRE_PHYSICO_CHIMIQUE');
        const hasLegacyPhysicoElements = hasPhysicoElements(sample);
        const hasPhysicoWork = hasPhysicoRoutedElements || hasLegacyPhysicoElements;
        if (!hasPhysicoWork) return false;
        
        // Needs analysis if not completed
        const needsAnalysis = !analysisCompleted;
        
        // Check if mechanical prep is done (or already progressed)
        const mechanicalPrepDone = sample.metadonnees?.mechanical_preparation_completed === true ||
                                   sample.statut !== SampleStatus.RECEPTION;
        
        return needsAnalysis && mechanicalPrepDone;
      });
      
      setSamples(physicoSamples);
      
      // Calculate stats
      const urgentSamples = physicoSamples.filter(
        (sample: Sample) => sample.priorite === Priority.URGENTE
      ).length;
      
      const inProgressSamples = physicoSamples.filter(
        (sample: Sample) => sample.metadonnees?.physico_chimique_results
      ).length;
      
      const completedToday = physicoSamples.filter((sample: Sample) => {
        const completedAt = sample.metadonnees?.physico_chimique_last_saved;
        if (!completedAt) return false;
        const today = new Date().toDateString();
        return new Date(completedAt).toDateString() === today;
      }).length;
      
      setStats({
        ready_for_analysis: physicoSamples.length,
        in_progress: inProgressSamples,
        completed_today: completedToday,
        urgent_samples: urgentSamples,
      });
      
    } catch (error) {
      console.error('Error loading samples:', error);
      setError('Erreur lors du chargement des échantillons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  // Toggle batch expansion
  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // Open batch for analysis
  const handleOpenBatch = (batchNumber: string, batchSamples: Sample[]) => {
    setSelectedBatch({ batchNumber, samples: batchSamples });
    
    // Initialize results for each sample in batch
    const initialResults: Record<string, PhysicoChimiqueResults> = {};
    batchSamples.forEach(sample => {
      const existingResults = sample.metadonnees?.physico_chimique_results;
      initialResults[sample.id] = existingResults || {
        densite: null,
        h2o: null,
        paf: null,
        mo: null,
        ph: null,
        blancheur_l: null,
        blancheur_a: null,
        blancheur_b: null,
        granulometrie: MESH_SIZES.map(size => ({
          size_um: size,
          mass_g: null,
          refus_percent: 0,
          cumulative_refus: 0,
          cumulative_passing: 100,
        })),
        d80: null,
        total_mass: null,
      };
    });
    setBatchResults(initialResults);
    setDialogOpen(true);
    setActiveTab(0);
  };

  // Open individual sample
  const handleOpenSample = (sample: Sample) => {
    setSelectedSample(sample);
    setSelectedBatch(null);
    
    const existingResults = sample.metadonnees?.physico_chimique_results;
    setBatchResults({
      [sample.id]: existingResults || {
        densite: null,
        h2o: null,
        paf: null,
        mo: null,
        ph: null,
        blancheur_l: null,
        blancheur_a: null,
        blancheur_b: null,
        granulometrie: MESH_SIZES.map(size => ({
          size_um: size,
          mass_g: null,
          refus_percent: 0,
          cumulative_refus: 0,
          cumulative_passing: 100,
        })),
        d80: null,
        total_mass: null,
      }
    });
    setDialogOpen(true);
    setActiveTab(0);
  };

  // Update result for a sample
  const updateSampleResult = (sampleId: string, field: string, value: number | null) => {
    setBatchResults(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [field]: value,
      }
    }));
  };

  // Calculate granulométrie values for a sample
  const calculateGranulometrie = (sampleId: string) => {
    const results = batchResults[sampleId];
    if (!results || !results.granulometrie) return;

    const granulo = [...results.granulometrie];
    const totalMass = granulo.reduce((sum, row) => sum + (row.mass_g || 0), 0);
    
    if (totalMass === 0) return;

    let cumulativeRefus = 0;
    
    for (let i = 0; i < granulo.length; i++) {
      const mass = granulo[i].mass_g || 0;
      const refusPercent = (mass / totalMass) * 100;
      cumulativeRefus += refusPercent;
      
      granulo[i] = {
        ...granulo[i],
        refus_percent: Math.round(refusPercent * 100) / 100,
        cumulative_refus: Math.round(cumulativeRefus * 100) / 100,
        cumulative_passing: Math.round((100 - cumulativeRefus) * 100) / 100,
      };
    }

    // Calculate D80
    let d80 = null;
    for (let i = 0; i < granulo.length - 1; i++) {
      const current = granulo[i];
      const next = granulo[i + 1];
      
      if (current.cumulative_passing >= 80 && next.cumulative_passing < 80) {
        const ratio = (80 - next.cumulative_passing) / (current.cumulative_passing - next.cumulative_passing);
        d80 = next.size_um + ratio * (current.size_um - next.size_um);
        break;
      }
    }

    setBatchResults(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        granulometrie: granulo,
        d80: d80 ? Math.round(d80) : null,
        total_mass: Math.round(totalMass * 100) / 100,
      }
    }));
  };

  // Save results
  const handleSaveResults = async (sendToValidation: boolean = false) => {
    try {
      setSaving(true);

      const samplesToProcess = selectedBatch 
        ? selectedBatch.samples 
        : (selectedSample ? [selectedSample] : []);

      for (const sample of samplesToProcess) {
        const results = batchResults[sample.id];
        if (!results) continue;

        const selectedElements = getPhysicoElementsFromSample(sample);
        const resultArray: any[] = [];

        if (results.densite !== null && selectedElements.includes('DENSITE')) {
          resultArray.push({ element: 'DENSITE', value: results.densite, unit: 'g/cm³', method: 'PRE.MO/ANA/047', status: 'VALIDE' });
        }
        if (results.h2o !== null && selectedElements.includes('H2O')) {
          resultArray.push({ element: 'H2O', value: results.h2o, unit: '%', method: 'PRE.MO/ANA/046', status: 'VALIDE' });
        }
        if (results.paf !== null && selectedElements.includes('PAF')) {
          resultArray.push({ element: 'PAF', value: results.paf, unit: '%', method: 'PRE.MO/ANA/050', status: 'VALIDE' });
        }
        if (results.mo !== null && selectedElements.includes('MO')) {
          resultArray.push({ element: 'MO', value: results.mo, unit: '%', method: 'PRE.MO/ANA/022', status: 'VALIDE' });
        }
        if (results.ph !== null && selectedElements.includes('PH')) {
          resultArray.push({ element: 'PH', value: results.ph, unit: '', method: 'PRE.MO/ANA/025', status: 'VALIDE' });
        }
        if (selectedElements.some((el: string) => el.startsWith('BLANCHEUR'))) {
          if (results.blancheur_l !== null) resultArray.push({ element: 'BLANCHEUR_L', value: results.blancheur_l, unit: '%', method: 'PRE.MO/ANA/045', status: 'VALIDE' });
          if (results.blancheur_a !== null) resultArray.push({ element: 'BLANCHEUR_A', value: results.blancheur_a, unit: '', method: 'PRE.MO/ANA/045', status: 'VALIDE' });
          if (results.blancheur_b !== null) resultArray.push({ element: 'BLANCHEUR_B', value: results.blancheur_b, unit: '', method: 'PRE.MO/ANA/045', status: 'VALIDE' });
        }
        if ((selectedElements.includes('GRANULO') || selectedElements.includes('D80')) && results.d80 !== null) {
          resultArray.push({
            element: 'D80', value: results.d80, unit: 'μm', method: 'PRE.MO/ANA/048', status: 'VALIDE',
            granulometrie_data: { mesh_sizes: results.granulometrie, total_mass: results.total_mass }
          });
        }

        // Save to sample metadata (store results in metadonnees like other labs do)
        const existingResults = sample.metadonnees?.analysis_results || [];
        // Filter out any existing Physico-Chimique results to avoid duplicates
        const physicoElements = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'D80', 'GRANULO'];
        const filteredResults = existingResults.filter((r: any) => !physicoElements.includes(r.element));
        
        await apiService.patch(`/samples/${sample.id}`, {
          metadonnees: {
            ...sample.metadonnees,
            physico_chimique_results: results,
            physico_chimique_last_saved: new Date().toISOString(),
            physico_chimique_analyst_id: user?.id,
            physico_chimique_analyst_name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            // 🔥 CRITICAL: Mark Physico-Chimique analysis as completed (for multi-lab workflow)
            ...(sendToValidation ? { 
              physico_chimique_analysis_completed: true,
              // Store results in analysis_results array for certificate generation
              analysis_results: [...filteredResults, ...resultArray]
            } : {}),
          }
        });
      }

      if (sendToValidation) {
        const sampleIds = samplesToProcess.map(s => s.id);
        
        // 🔥 MULTI-LAB: Only move to validation if ALL labs are complete
        // For now, move to validation - the validation service will check if all labs are done
        await samplesService.bulkUpdateSampleStatus(sampleIds, SampleStatus.VALIDATION_SERVICE, 'Analyse Physico-Chimique terminée');
        setSnackbar({ open: true, message: 'Résultats envoyés pour validation', severity: 'success' });
        setDialogOpen(false);
        loadSamples();
      } else {
        setSnackbar({ open: true, message: 'Résultats sauvegardés', severity: 'success' });
      }

    } catch (error) {
      console.error('Error saving results:', error);
      setSnackbar({ open: true, message: 'Erreur lors de la sauvegarde', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      default: return 'default';
    }
  };

  const { batches, individualSamples } = groupSamplesByBatch(samples);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Chargement du tableau de bord Physico-Chimique...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Science color="primary" sx={{ fontSize: 40 }} />
            Tableau de Bord - Analyste Physico-Chimique
            <Chip label={`${samples.length} échantillons`} color="primary" variant="outlined" />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Densité, H₂O, PAF, MO, pH, Blancheur, Granulométrie
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadSamples} disabled={loading}>
          Actualiser
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`, borderLeft: `6px solid ${theme.palette.primary.main}` }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Prêts pour analyse</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">{stats.ready_for_analysis}</Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`, borderLeft: `6px solid ${theme.palette.warning.main}` }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">En cours</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.in_progress}</Typography>
                </Box>
                <Timer sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`, borderLeft: `6px solid ${theme.palette.success.main}` }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Terminés aujourd'hui</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">{stats.completed_today}</Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`, borderLeft: `6px solid ${theme.palette.error.main}` }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Échantillons urgents</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">{stats.urgent_samples}</Typography>
                </Box>
                <PriorityHigh sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Batches/Lots Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment color="primary" />
            Lots en attente d'analyse
          </Typography>

          {Object.keys(batches).length === 0 && individualSamples.length === 0 ? (
            <Alert severity="info">Aucun lot avec des éléments Physico-Chimiques en attente d'analyse</Alert>
          ) : (
            <Box>
              {/* Batches */}
              {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                const isExpanded = expandedBatches.has(batchNumber);
                const requestedElements = getPhysicoElementsFromSample(batchSamples[0]);
                const hasSavedResults = batchSamples.some(s => s.metadonnees?.physico_chimique_results);
                
                return (
                  <Accordion key={batchNumber} expanded={isExpanded} onChange={() => toggleBatchExpansion(batchNumber)}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          📦 Lot {batchNumber}
                        </Typography>
                        <Chip label={`${batchSamples.length} échantillons`} size="small" color="primary" />
                        <Stack direction="row" spacing={0.5}>
                          {requestedElements.slice(0, 5).map(el => (
                            <Chip key={el} label={el} size="small" variant="outlined" />
                          ))}
                          {requestedElements.length > 5 && (
                            <Chip label={`+${requestedElements.length - 5}`} size="small" />
                          )}
                        </Stack>
                        {hasSavedResults && <Chip label="En cours" color="warning" size="small" icon={<Timer />} />}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<Science />}
                          onClick={(e) => { e.stopPropagation(); handleOpenBatch(batchNumber, batchSamples); }}
                        >
                          Analyser le lot
                        </Button>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID Échantillon</TableCell>
                              <TableCell>Position</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Priorité</TableCell>
                              <TableCell>État</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {batchSamples.map(sample => (
                              <TableRow key={sample.id}>
                                <TableCell>{sample.sample_id}</TableCell>
                                <TableCell>{sample.metadonnees?.batch_info?.sample_position || '-'}</TableCell>
                                <TableCell>{sample.client?.company_name || '-'}</TableCell>
                                <TableCell>
                                  <Chip label={sample.priorite} size="small" color={getPriorityColor(sample.priorite)} />
                                </TableCell>
                                <TableCell>
                                  {sample.metadonnees?.physico_chimique_results ? (
                                    <Chip label="En cours" color="warning" size="small" />
                                  ) : (
                                    <Chip label="À faire" size="small" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {/* Individual Samples */}
              {individualSamples.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Échantillons individuels
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Éléments</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>État</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {individualSamples.map(sample => {
                          const requestedElements = getPhysicoElementsFromSample(sample);
                          return (
                            <TableRow key={sample.id}>
                              <TableCell><Typography fontWeight="bold">{sample.sample_id}</Typography></TableCell>
                              <TableCell>{sample.client?.company_name || '-'}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  {requestedElements.map(el => <Chip key={el} label={el} size="small" variant="outlined" />)}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip label={sample.priorite} size="small" color={getPriorityColor(sample.priorite)} />
                              </TableCell>
                              <TableCell>
                                {sample.metadonnees?.physico_chimique_results ? (
                                  <Chip label="En cours" color="warning" size="small" />
                                ) : (
                                  <Chip label="À faire" size="small" />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Analyser">
                                  <IconButton color="primary" onClick={() => handleOpenSample(sample)}>
                                    <Science />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Analysis Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              🧪 Analyse Physico-Chimique - {selectedBatch ? `Lot ${selectedBatch.batchNumber}` : selectedSample?.sample_id}
            </Typography>
            {saving && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="Mesures Simples" />
            <Tab label="Blancheur (L*a*b*)" />
            <Tab label="Granulométrie" />
          </Tabs>

          {/* Tab 1: Simple Measurements - Batch table */}
          <TabPanel value={activeTab} index={0}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon</TableCell>
                    <TableCell>Densité (g/cm³)</TableCell>
                    <TableCell>H₂O (%)</TableCell>
                    <TableCell>PAF (%)</TableCell>
                    <TableCell>MO (%)</TableCell>
                    <TableCell>pH</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedBatch?.samples || (selectedSample ? [selectedSample] : [])).map(sample => {
                    const results = batchResults[sample.id] || {};
                    const selectedElements = getPhysicoElementsFromSample(sample);
                    return (
                      <TableRow key={sample.id}>
                        <TableCell><Typography fontWeight="bold">{sample.sample_id}</Typography></TableCell>
                        <TableCell>
                          {selectedElements.includes('DENSITE') ? (
                            <TextField size="small" type="number" value={results.densite ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'densite', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {selectedElements.includes('H2O') ? (
                            <TextField size="small" type="number" value={results.h2o ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'h2o', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {selectedElements.includes('PAF') ? (
                            <TextField size="small" type="number" value={results.paf ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'paf', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {selectedElements.includes('MO') ? (
                            <TextField size="small" type="number" value={results.mo ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'mo', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {selectedElements.includes('PH') ? (
                            <TextField size="small" type="number" value={results.ph ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'ph', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01, min: 0, max: 14 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Tab 2: Blancheur - Batch table */}
          <TabPanel value={activeTab} index={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Espace couleur CIE L*a*b* : L* (luminance), a* (rouge-vert), b* (jaune-bleu)
            </Alert>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon</TableCell>
                    <TableCell>L* (Luminance %)</TableCell>
                    <TableCell>a* (Rouge/Vert)</TableCell>
                    <TableCell>b* (Jaune/Bleu)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedBatch?.samples || (selectedSample ? [selectedSample] : [])).map(sample => {
                    const results = batchResults[sample.id] || {};
                    const selectedElements = getPhysicoElementsFromSample(sample);
                    const hasBlancheur = selectedElements.some((el: string) => el.startsWith('BLANCHEUR'));
                    return (
                      <TableRow key={sample.id}>
                        <TableCell><Typography fontWeight="bold">{sample.sample_id}</Typography></TableCell>
                        <TableCell>
                          {hasBlancheur ? (
                            <TextField size="small" type="number" value={results.blancheur_l ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'blancheur_l', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {hasBlancheur ? (
                            <TextField size="small" type="number" value={results.blancheur_a ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'blancheur_a', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {hasBlancheur ? (
                            <TextField size="small" type="number" value={results.blancheur_b ?? ''} 
                              onChange={(e) => updateSampleResult(sample.id, 'blancheur_b', e.target.value === '' ? null : parseFloat(e.target.value))}
                              inputProps={{ step: 0.01 }} sx={{ width: 100 }} />
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Tab 3: Granulométrie - Per sample */}
          <TabPanel value={activeTab} index={2}>
            {(selectedBatch?.samples || (selectedSample ? [selectedSample] : [])).map(sample => {
              const results = batchResults[sample.id] || {};
              const selectedElements = getPhysicoElementsFromSample(sample);
              const hasGranulo = selectedElements.includes('GRANULO') || selectedElements.includes('D80');
              
              if (!hasGranulo) return null;
              
              return (
                <Accordion key={sample.id} defaultExpanded={!selectedBatch}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight="bold">{sample.sample_id}</Typography>
                    {results.d80 && <Chip label={`D80: ${results.d80} μm`} color="success" size="small" sx={{ ml: 2 }} />}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="contained" startIcon={<Calculate />} onClick={() => calculateGranulometrie(sample.id)}>
                        Calculer
                      </Button>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Maille (μm)</TableCell>
                            <TableCell>Masse (g)</TableCell>
                            <TableCell>% Passant Cumulé</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {results.granulometrie?.map((row, index) => (
                            <TableRow key={row.size_um}>
                              <TableCell>{row.size_um === -25 ? '< 25' : row.size_um}</TableCell>
                              <TableCell>
                                <TextField size="small" type="number" value={row.mass_g ?? ''}
                                  onChange={(e) => {
                                    const newGranulo = [...(results.granulometrie || [])];
                                    newGranulo[index] = { ...newGranulo[index], mass_g: e.target.value === '' ? null : parseFloat(e.target.value) };
                                    setBatchResults(prev => ({ ...prev, [sample.id]: { ...prev[sample.id], granulometrie: newGranulo } }));
                                  }}
                                  inputProps={{ step: 0.01, min: 0 }} sx={{ width: 80 }} />
                              </TableCell>
                              <TableCell>{row.cumulative_passing?.toFixed(2) ?? '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Masse totale:</Typography>
                          <Typography variant="h6" fontWeight="bold">{results.total_mass ?? '-'} g</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">D80:</Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.main">{results.d80 ? `${results.d80} μm` : '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="outlined" startIcon={<Save />} onClick={() => handleSaveResults(false)} disabled={saving}>
            Sauvegarder (continuer plus tard)
          </Button>
          <Button variant="contained" color="success" startIcon={<Send />} onClick={() => handleSaveResults(true)} disabled={saving}>
            Envoyer pour validation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PhysicoChimiqueAnalystDashboard;


